import { AxiosError } from 'axios'
import produce from 'immer'
import React from 'react'
import { QueryStatus, QueryObserverOptions, UseQueryResult } from 'react-query'
import { UseInfiniteQueryResult } from 'react-query/types/react/types'
import create, { State } from 'zustand'

import { PagedUserDefinedServiceResponse } from 'types/api/services/dependencies/dependency'
import { UserDefinedService } from 'types/api/services/service'

import { useGremlinPagination, useGremlinQuery } from './use-gremlin-query'

export interface ServicesState extends State {
  status: QueryStatus
  services: {
    [id: string]: UserDefinedService
  }
  hasNextPage: boolean
  set: (fn: (state: ServicesState) => void) => void
  clearAll: () => void
}

export const useServicesStore = create<ServicesState>((set) => ({
  status: 'loading',
  services: {},
  hasNextPage: true,
  set: (fn) => set(produce(fn)),
  clearAll: () =>
    set(
      produce((state) => {
        state.status = 'loading'
        state.services = {}
      })
    )
}))

export function useServices(
  enabled = true
): UseInfiniteQueryResult<PagedUserDefinedServiceResponse> {
  const set = useServicesStore((state) => state.set)
  const pagedAllFetchState =
    useGremlinPagination<PagedUserDefinedServiceResponse>(
      // Use long form endpoint to ensure react-query key cache hit
      '/services/?pageSize=20',
      {},
      {
        onSuccess: (data) => {
          set((state) => {
            const all = data?.pages?.flatMap((page) => page.items)
            state.services = {
              ...Object.fromEntries(all.map((s) => [s.serviceId, s]))
            }
          })
        },
        onSettled: (data, error) => {
          set((state) => {
            state.status =
              error && (!data || typeof data === 'string') ? 'error' : 'success'
          })
        },
        refetchInterval: 60_000,
        staleTime: 60_000,
        enabled
      }
    )

  React.useEffect(() => {
    let timeout
    if (
      pagedAllFetchState.hasNextPage &&
      !pagedAllFetchState.isFetchingNextPage
    ) {
      set((state) => {
        state.hasNextPage = true
      })
      timeout = setTimeout(() => {
        pagedAllFetchState.fetchNextPage()
      }, 200)
    } else if (!pagedAllFetchState.hasNextPage) {
      set((state) => {
        state.hasNextPage = false
      })
    }
    return () => clearTimeout(timeout)
  }, [pagedAllFetchState, set])

  return pagedAllFetchState
}

export function useGetService(
  id: string,
  queryOptions?: Partial<QueryObserverOptions<UserDefinedService, AxiosError>>
): UseQueryResult<UserDefinedService> {
  const set = useServicesStore((state) => state.set)
  const initialData = useServicesStore((state) => state.services[id])
  const serviceFetchState = useGremlinQuery<UserDefinedService>(
    `/services/${id}`,
    {},
    {
      placeholderData: initialData,
      enabled: Boolean(id),
      ...queryOptions,
      onSuccess: (data) => {
        set((state) => {
          state.services[data.serviceId] = data
        })
      }
    }
  )

  return serviceFetchState
}
